import { INTENT_TYPE } from '../../constants';
import { isWholeQueryAtom } from '../features/protected-atoms';
import { segmentWordTokens } from '../features/word-tokens';
import { type ConvergenceRule, type FeatureContext } from '../types';

/**
 * `S-STRUCTURED` (priority 990) — the whole query is one structured thing.
 *
 * The first of the final Search protections, and mandatory. A user who pasted a
 * URL, an ARI, or a quoted exact title has already told us what they want; Chat
 * rules and the residual model cannot override it.
 *
 * The atom must span the **entire** query. `explain https://…` is a Chat request
 * about a URL, not structured input, and it is the operand-evidence path that
 * should claim it.
 *
 * A quoted range must contain a word-like token: `""` and `"  "` are mid-typing
 * artefacts, not titles.
 */
export const isWholeQueryStructured = (features: FeatureContext): boolean => {
	const atom = isWholeQueryAtom(features.qMatch, features.protectedAtoms);

	if (!atom) {
		return false;
	}

	if (atom.kind !== 'QUOTED') {
		return true;
	}

	// Strip the surrounding quote pair before asking whether a title is really there.
	const inner = atom.text.slice(1, -1);

	return segmentWordTokens(inner).tokens.length > 0;
};

export const structuredRule: ConvergenceRule = {
	type: INTENT_TYPE.FPS,
	pattern: 'STRUCTURED_INPUT',
	matches: isWholeQueryStructured,
};
